import { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import { Search, Close, Edit, Work, School as SchoolIcon, Phone, Email } from '@mui/icons-material';
import axios from 'axios';

interface Alumni {
  id: string;
  student_id: string;
  title: string;
  first_name_th: string;
  last_name_th: string;
  current_first_name_th?: string;
  current_last_name_th?: string;
  education: {
    degree: string;
    major: string;
    faculty: string;
    graduation_year: number;
  };
  current_occupation?: string;
  contact: {
    email: string;
    phone: string;
  };
  profile_photo?: {
    url: string;
  };
  batch?: string;
}

const AlumniSearchSection = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [alumni, setAlumni] = useState<Alumni[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedAlumni, setSelectedAlumni] = useState<Alumni | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  const API_URL = import.meta.env.VITE_API_URL || 'https://stardust-aims.amy.care';

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      setError('กรุณากรอกคำค้นหา');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_URL}/api/v1/alumni/public/search`, {
        params: {
          search: searchTerm,
          limit: 12,
        },
      });
      setAlumni(response.data.data || []);
      if (response.data.data.length === 0) {
        setError('ไม่พบข้อมูลศิษย์เก่าที่ตรงกับคำค้นหา');
      }
    } catch (err) {
      console.error('Error searching alumni:', err);
      setError('เกิดข้อผิดพลาดในการค้นหา กรุณาลองใหม่อีกครั้ง');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleViewDetails = (alumniData: Alumni) => {
    setSelectedAlumni(alumniData);
    setDetailDialogOpen(true);
  };

  const handleUpdateProfile = () => {
    // Redirect to admin portal for profile update
    window.location.href = 'https://admin-aims.amy.care/login';
  };

  const getDisplayName = (alumniData: Alumni) => {
    const firstName = alumniData.current_first_name_th || alumniData.first_name_th;
    const lastName = alumniData.current_last_name_th || alumniData.last_name_th;
    return `${alumniData.title}${firstName} ${lastName}`;
  };

  return (
    <Box id="alumni-search-section" sx={{ py: 8, backgroundColor: 'background.paper' }}>
      <Container maxWidth="xl">
        <Typography
          variant="h3"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            mb: 1,
            textAlign: 'center',
          }}
        >
          ค้นหาศิษย์เก่า
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: 'text.secondary',
            mb: 6,
            textAlign: 'center',
          }}
        >
          ค้นหาข้อมูลศิษย์เก่าและติดต่อเพื่อน ๆ
        </Typography>

        {/* Search Bar */}
        <Box sx={{ maxWidth: 600, mx: 'auto', mb: 6 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={9}>
              <TextField
                fullWidth
                placeholder="ค้นหาด้วยชื่อ, รหัสนักศึกษา, หรือคณะ..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={handleKeyPress}
                variant="outlined"
                sx={{
                  backgroundColor: 'white',
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<Search />}
                onClick={handleSearch}
                disabled={loading}
                sx={{
                  height: '56px',
                  backgroundColor: 'secondary.main',
                  color: 'secondary.contrastText',
                  '&:hover': {
                    backgroundColor: 'secondary.dark',
                  },
                }}
              >
                ค้นหา
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Error State */}
        {error && !loading && (
          <Alert severity={alumni.length === 0 ? 'info' : 'error'} sx={{ mb: 4 }}>
            {error}
          </Alert>
        )}

        {/* Results */}
        {!loading && alumni.length > 0 && (
          <Grid container spacing={3}>
            {alumni.map((item) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 16px rgba(0,0,0,0.12)',
                    },
                  }}
                  onClick={() => handleViewDetails(item)}
                >
                  <CardContent sx={{ textAlign: 'center', flexGrow: 1 }}>
                    <Avatar
                      src={item.profile_photo?.url}
                      sx={{
                        width: 100,
                        height: 100,
                        mx: 'auto',
                        mb: 2,
                        border: '3px solid',
                        borderColor: 'primary.light',
                      }}
                    >
                      {getDisplayName(item).charAt(0)}
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      {getDisplayName(item)}
                    </Typography>
                    <Chip
                      label={item.student_id}
                      size="small"
                      sx={{ mb: 2, fontWeight: 500 }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {item.education.faculty}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {item.education.major}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      รุ่นปี {item.education.graduation_year}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Detail Dialog */}
        <Dialog
          open={detailDialogOpen}
          onClose={() => setDetailDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          {selectedAlumni && (
            <>
              <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6">ข้อมูลศิษย์เก่า</Typography>
                  <IconButton onClick={() => setDetailDialogOpen(false)}>
                    <Close />
                  </IconButton>
                </Box>
              </DialogTitle>
              <DialogContent dividers>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Avatar
                    src={selectedAlumni.profile_photo?.url}
                    sx={{
                      width: 120,
                      height: 120,
                      mx: 'auto',
                      mb: 2,
                      border: '4px solid',
                      borderColor: 'primary.light',
                    }}
                  >
                    {getDisplayName(selectedAlumni).charAt(0)}
                  </Avatar>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                    {getDisplayName(selectedAlumni)}
                  </Typography>
                  <Chip label={selectedAlumni.student_id} sx={{ fontWeight: 500 }} />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <SchoolIcon color="primary" />
                    <Typography variant="subtitle2" color="text.secondary">
                      การศึกษา
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ ml: 4 }}>
                    {selectedAlumni.education.degree} - {selectedAlumni.education.major}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                    {selectedAlumni.education.faculty}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                    สำเร็จการศึกษาปี {selectedAlumni.education.graduation_year}
                  </Typography>
                </Box>

                {selectedAlumni.current_occupation && (
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Work color="primary" />
                      <Typography variant="subtitle2" color="text.secondary">
                        อาชีพปัจจุบัน
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ ml: 4 }}>
                      {selectedAlumni.current_occupation}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Phone color="primary" />
                    <Typography variant="subtitle2" color="text.secondary">
                      ติดต่อ
                    </Typography>
                  </Box>
                  {selectedAlumni.contact.email && (
                    <Typography variant="body2" sx={{ ml: 4, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Email fontSize="small" />
                      {selectedAlumni.contact.email}
                    </Typography>
                  )}
                  {selectedAlumni.contact.phone && (
                    <Typography variant="body2" sx={{ ml: 4, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Phone fontSize="small" />
                      {selectedAlumni.contact.phone}
                    </Typography>
                  )}
                </Box>
              </DialogContent>
              <DialogActions sx={{ p: 2 }}>
                <Button onClick={() => setDetailDialogOpen(false)}>
                  ปิด
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Edit />}
                  onClick={handleUpdateProfile}
                  sx={{
                    backgroundColor: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                  }}
                >
                  อัพเดทข้อมูลของฉัน
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Container>
    </Box>
  );
};

export default AlumniSearchSection;
